import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { api } from "../../utils/api.js";
import { ENDPOINTS as E } from "../../utils/endpoints.js";
import ClientTable from "./ClientTable.js";
import CustomSelect from "./CustomSelect.js";
import avatar from "../../vendor/avatar.svg";
import vacsList from "../../vendor/vacs-list.svg";
import people from "../../vendor/people.svg";
import ava from "../../vendor/ava.svg";
import up from "../../vendor/up.svg";
import down from "../../vendor/down.svg";
import search from "../../vendor/search.svg";
import close from "../../vendor/close2.svg";
import back from "../../vendor/back.svg";
import "./ClientRoom.css";

function ClientData({
  freshUserInfo,
  isVacanciesOpened,
  vacancies,
  responds,
  image,
  ink,
  setVacanciesOpened,
  setResponds,
  setImage,
  refreshData,
  onRegisterFunction
}) {
  const [findedText, setFindedText] = useState("");
  const [currentData, setCurrentData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [findedKeys, setFindedKeys] = useState([]);
  const [currentVacancy, setCurrentVacancy] = useState("all");
  const [currentStatus, setCurrentStatus] = useState("all");
  const [currentRenderVal, setCurrentRenderVal] = useState(0);

  const fileInputRef = useRef(null);

  const userStatuses = useMemo(
    () => ["Новый кандидат", "Рассматриваем", "Собеседование", "Отказ"],
    [],
  );

  const updateData = useCallback(
    (e) => {
      const val = e ? e.target.value : findedText;
      setFindedText(val);
      const fil = currentData
        .filter((i) =>
          isVacanciesOpened || currentVacancy === "all"
            ? true
            : i.vacancy &&
              i.vacancy.trim().toLowerCase() ===
                currentVacancy.trim().toLowerCase(),
        )
        .filter((i) => {
          if (isVacanciesOpened || currentStatus === "all") return true;
          if (i.user_status === null || i.user_status === undefined)
            return false;

          const statusText = userStatuses[Number(i.user_status)];

          if (!statusText) return false;

          return (
            statusText.trim().toLowerCase() ===
            currentStatus.trim().toLowerCase()
          );
        })
        .filter((item) => {
          return findedKeys.some((key) => {
            if (item[key] !== undefined && item[key] !== null) {
              return String(item[key])
                .toLowerCase()
                .includes(val.toLowerCase());
            }
            return false;
          });
        });
      setFilteredData(fil);
    },
    [
      currentData,
      currentVacancy,
      currentStatus,
      findedKeys,
      findedText,
      isVacanciesOpened,
      userStatuses,
    ],
  );

  useEffect(() => {
    updateData();
  }, [isVacanciesOpened, updateData]);

  useEffect(() => {
    if (vacancies || responds) {
      const vacanciesKeys = ["title", "address"];
      const respondsKeys = ["vacancy", "name", "city", "client_comment"];
      setCurrentData(isVacanciesOpened ? vacancies : responds);
      setFindedKeys(isVacanciesOpened ? vacanciesKeys : respondsKeys);
    }
  }, [isVacanciesOpened, vacancies, responds]);

  const renderCount = useCallback((item, column, value, rowIndex) => {
    const { count, old_cnt } = item;
    const newCount = count - old_cnt;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          color: "#ff5900",
        }}
      >
        <span style={{ marginRight: "20px", fontWeight: "400" }}>
          {old_cnt}
        </span>
        {+newCount > 0 ? (
          <span
            style={{
              width: "max-content",
              borderRadius: "15px",
              backgroundColor: "#f2f2f2",
              padding: "5px",
            }}
          >
            (+{newCount})
          </span>
        ) : null}
      </div>
    );
  }, []);

  const renderName = useCallback((item, column, value, rowIndex) => {
    if (!item) {
      return undefined;
    }

    return (
      <div
        style={{
          display: "flex",
          paddingLeft: "15px",
          flexDirection: "column",
        }}
      >
        <span className="data-table-title data-table-title_vacs">{value}</span>
        {item.vacancy && (
          <span
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              color: "#ff5900",
              overflow: "hidden",
              maxHeight: "21px",
              textOverflow: "ellipsis",
            }}
            title={item.vacancy}
          >
            {item.vacancy}
          </span>
        )}
      </div>
    );
  }, []);

  function chooseVacancy(item) {
    setVacanciesOpened(false);
    setCurrentVacancy(item.title);
    setCurrentRenderVal(vacancies.findIndex((i) => item.title === i.title) + 2);
  }

  const changeStatus = useCallback(
    (newValue, item) => {
      const current = responds.find((dataItem) => dataItem.id === item.id);
      const target = {
        id: +current.id,
        status: newValue,
      };
      setResponds(target);
    },
    [responds, setResponds],
  );

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const convertToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match("image.*")) {
      alert(`Вы пытаетесь загрузить файл типа - ${file.type}, который не поддерживается.
         Пожалуйста, выберите файл изображения (PNG, JPG, JPEG, GIF)`);
      return;
    }
    const maxSize = 1024 * 1024;
    if (file.size > maxSize) {
      alert(`Загружаемый вами файл слишком большой - ${
        Math.floor((file.size / maxSize) * 10) / 10
      }Мб,
      в то время к максимальный размер - 1Мб.
      Пожалуйста загрузите файл меньшего размера.`);
      return;
    }

    try {
      const base64 = await convertToBase64(file);
      const base64Data = base64.replace(/^data:image\/\w+;base64,/, "");
      const target = {
        type: file.type,
        img_src: base64Data,
      };
      const response = await api.post(E.GET_CLIENT_INFO, 1, {
        data: [ink, target],
      });

      if (response.success) {
        const url = URL.createObjectURL(file);
        setImage(url);

        if (image && image.startsWith("blob:")) {
          URL.revokeObjectURL(image);
        }
      } else {
        console.error("Ошибка загрузки:", response.error);
        alert(`Ошибка загрузки: ${response.error || "Неизвестная ошибка"}`);
      }
    } catch (error) {
      console.error("Ошибка при загрузке файла:", error);
      alert("Произошла ошибка при загрузке файла");
    } finally {
      event.target.value = "";
    }
  };

  const toggleVacsOpened = useCallback((val) => {
    setFindedKeys([]);
    setCurrentVacancy("all");
    setCurrentStatus("all");
    setVacanciesOpened(val);

    if (refreshData) {
      refreshData();
    }

    if (!val) {
      setCurrentRenderVal(0);
      setFilteredData(currentData);
    }
  }, [currentData, refreshData, setVacanciesOpened])

  useEffect(() => {
    if (onRegisterFunction) {
      onRegisterFunction(() => {toggleVacsOpened(true)})
    }
  }, [onRegisterFunction, toggleVacsOpened]);

  function checkShowed() {
    api.post(E.MARK_SHOWED, 1, { data: { ink, vac: currentVacancy } });
  }

  return (
    <section className="ClientRoom">
      <aside className="ClientRoom_menu">
        <div className="ClientRoom_user">
          <img
            src={image || avatar}
            alt="Иконка"
            className="icon icon_avatar icon_background"
            style={
              image
                ? {
                    border: "1px solid #b9b9b9",
                    boxSizing: "border-box",
                    objectFit: "contain",
                  }
                : {}
            }
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          <div
            className="icon_avatar icon_button_background"
            onClick={handleUpload}
            title="Поддерживаются изображения размером не более 1Мб"
          >
            <img
              src={ava}
              alt="Иконка"
              className={`icon icon_button`}
              style={{
                width: "47px",
                height: "46px",
                marginLeft: 0,
              }}
            />
          </div>
          <span>{freshUserInfo.name || "Название компании"}</span>
        </div>
        <div className="ClientRoom_links">
          <div
            onClick={() => {
              if (!isVacanciesOpened) {
                toggleVacsOpened(true);
              }
            }}
          >
            <img
              src={vacsList}
              alt="Вакансии"
              className="icon ClientRoom_links__icon"
            />
            <span style={{ color: isVacanciesOpened ? "#ff5900" : "#000" }}>
              Вакансии
            </span>
          </div>
        </div>
        <div className="ClientRoom_links">
          <div
            onClick={() => {
              if (isVacanciesOpened) {
                toggleVacsOpened(false);
              }
            }}
          >
            <img
              src={people}
              alt="Отклики"
              className="icon ClientRoom_links__icon"
            />
            <span style={{ color: !isVacanciesOpened ? "#ff5900" : "#000" }}>
              Отклики
            </span>
          </div>
        </div>
      </aside>
      <article className="ClientRoom_data">
        {!isVacanciesOpened ? (
          <div className="ClientRoom_additional-btns">
            <img
              src={back}
              alt="назад"
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                toggleVacsOpened(true);
              }}
            />
            {filteredData.length ? (
              <p
                style={{
                  fontWeight: "400",
                  fontSize: "14px",
                  color: "var(--bg-color2)",
                  cursor: "pointer",
                  margin: "auto 0",
                }}
                onClick={checkShowed}
              >
                {`Отметить все отклики ${
                  currentVacancy !== "all" ? "по данной вакансии" : ""
                } просмотренными`}
              </p>
            ) : null}
          </div>
        ) : null}
        {isVacanciesOpened ? (
          <h2 className="ClientRoom_title">Вакансии</h2>
        ) : null}
        <div
          className={`ClientRoom_filters ${
            isVacanciesOpened && "ClientRoom_filters__vacs"
          }`}
        >
          <div
            className={`ClientRoom_search ${
              !isVacanciesOpened && "ClientRoom_search_long"
            }`}
          >
            <input
              type="text"
              id="input_search"
              value={findedText}
              onChange={updateData}
              placeholder="Поиск"
            />
            <img
              className={`input_search ${
                findedText && findedText.length > 0 && "input_search_edited"
              }`}
              src={findedText && findedText.length > 0 ? close : search}
              alt={findedText && findedText.length > 0 ? "Очистить" : "Поиск"}
              title={findedText && findedText.length > 0 ? "Очистить" : "Поиск"}
              style={{ left: `${(findedText?.length || 0) * 8.2 + 70}px` }}
              onClick={
                findedText && findedText.length > 1
                  ? () => {
                      setFindedText("");
                    }
                  : null
              }
            />
          </div>
          {!isVacanciesOpened ? (
            <div className="ClientRoom_options">
              <CustomSelect
                currentVal={currentRenderVal}
                params={{
                  buttonClass: "ClientRoom_options_select",
                  buttonClassOpened: "ClientRoom_options_select__open",
                  optionsClassName: {
                    0: "hidden",
                    base: "ClientRoom_options_select_option",
                    collapse: "ClientRoom_options_collapse",
                  },
                  arrow: { isNeed: true, close: up, open: down },
                }}
                options={[
                  "Выберите вакансию",
                  "Все вакансии",
                  ...vacancies.map((item) => item.title),
                ]}
                onChange={(newValue) => {
                  if (newValue < 2) {
                    setCurrentVacancy("all");
                  } else {
                    setCurrentVacancy(
                      vacancies.map((item) => item.title)[newValue - 2],
                    );
                  }
                }}
              />
              <CustomSelect
                params={{
                  buttonClass: "ClientRoom_options_select",
                  buttonClassOpened: "ClientRoom_options_select__open",
                  optionsClassName: {
                    0: "hidden",
                    base: "ClientRoom_options_select_option",
                    collapse: "ClientRoom_options_collapse",
                  },
                  arrow: { isNeed: true, close: up, open: down },
                }}
                options={["Выберите статус", "Все статусы", ...userStatuses]}
                onChange={(newValue) => {
                  if (newValue < 2) {
                    setCurrentStatus("all");
                  } else {
                    setCurrentStatus(userStatuses[newValue - 2]);
                  }
                }}
              />
            </div>
          ) : null}
          {!isVacanciesOpened ? (
            <h2 className="ClientRoom_title">Отклики</h2>
          ) : null}
        </div>
        {isVacanciesOpened ? (
          <ClientTable
            data={filteredData}
            onRowClick={chooseVacancy}
            columns={[
              {
                key: "title",
                title: "Вакансия",
                sortable: true,
                align: "left",
                className: "data-table-title",
                bodyClassName: "data-table-title_vacs data-table-title_shift",
              },
              {
                key: "address",
                title: "Регион",
                sortable: true,
                bodyClassName: "data-table-title_shift",
              },
              {
                key: "count",
                title: "Отклики",
                sortable: true,
                format: "number",
                bodyClassName: "data-table-title_shift",
                renderCell: renderCount,
              },
            ]}
          />
        ) : (
          <ClientTable
            data={filteredData}
            needExpandedRows={true}
            expandedCallback={setResponds}
            columns={[
              {
                key: "name",
                title: "ФИО",
                sortable: true,
                renderCell: renderName,
              },
              {
                key: "phone",
                title: "Телефон",
                format: "phone",
                sortable: true,
                bodyClassName: "data-table-title_shift",
                width: { min: "128px" },
              },
              {
                key: "age",
                title: "Возраст",
                sortable: true,
                format: "number",
                bodyClassName: "data-table-title_shift",
              },
              {
                key: "ts",
                title: "Дата",
                sortable: true,
                bodyClassName: "data-table-title_shift",
                format: "date",
              },
              {
                key: "user_status",
                title: "Статус",
                sortable: true,
                bodyClassName: "data-table-title_shift",
                format: "enum",
                enum: userStatuses,
                onCellClick: changeStatus,
                width: { strict: "139px" },
              },
            ]}
          />
        )}
      </article>
    </section>
  );
}

export default ClientData;
